import cx from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import Sentinel from '@/common/Sentinel/Sentinel';
import SuggestedFilterChips from '@/common/SuggestedFilterChips';
import TwoRowSuggestedFilters from '@/srp/MainLayout/TwoRowSuggestedFilters';
import { useIsDesktop } from '~/context/common/DeviceContext';
import SuggestedFiltersContext from '~/context/srp/SuggestedFiltersContext';
import useSuggestedFilterChipItems from '~/hooks/srp/useSuggestedFilterChipItems';
import useTrackSuggestedFilterPositions from '~/hooks/srp/useTrackSuggestedFilterPositions';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import {
  containerNegativeMargin,
  containerPadding
} from '../MainLayout.styles';

type OwnProps = {
  className?: string;
};

function QuickFilters({ className }: OwnProps) {
  const { suggestedFilters } = useContext(SuggestedFiltersContext);
  const {
    isLoading,
    items,
    show2RowSuggestedFilters,
    showCounts,
    showSuggestedFilters
  } = useSuggestedFilterChipItems();
  const [isStuck, setIsStuck] = useState(false);
  const [isDeepStuck, setIsDeepStuck] = useState(false);
  const isDesktop = useIsDesktop();
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useTrackSuggestedFilterPositions(
    scrollRef,
    show2RowSuggestedFilters ? 0 : items.length
  );

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollLeft = 0;
  }, [scrollRef, suggestedFilters.isLoading]);

  if (show2RowSuggestedFilters) {
    const chipItems = showSuggestedFilters ? items : [];
    const chipIsLoading = showSuggestedFilters && isLoading;
    const hasNoSuggestedFilters = !chipIsLoading && chipItems.length === 0;

    return (
      <>
        <Sentinel
          observerOptions={{
            root: null,
            rootMargin: '-24px 0px 0px 0px',
            threshold: 0
          }}
          onIntersectionChange={isVisible => setIsStuck(!isVisible)}
          testId="srp-quick-filters-sticky-sentinel"
        />
        <Sentinel
          observerOptions={{
            root: null,
            rootMargin: '0px 0px 0px 0px',
            threshold: 0
          }}
          onIntersectionChange={isVisible => setIsDeepStuck(!isVisible)}
          testId="srp-quick-filters-deep-sticky-sentinel"
        />
        <div
          className={cx(
            className,
            '-mx-2xl pl-2xl pr-0 pt-xl sm:-mx-3xl sm:pl-3xl sm:pr-0 sm:pt-xl md:-mx-xl md:px-xl md:pt-2xl lg:-mx-2xl lg:px-2xl',
            'sticky top-0 z-1 flex min-w-0 items-start gap-md transition-colors duration-150 md:top-xl md:rounded-t-md',
            isStuck
              ? 'bg-background-primary-subtle'
              : ['bg-background-default', 'md:rounded-b-md'],
            { hidden: hasNoSuggestedFilters && isDesktop }
          )}
          data-testid="srp-quick-filters"
        >
          {!hasNoSuggestedFilters && (
            <div
              className={cx(
                'absolute -top-xl inset-x-0 hidden h-xl transition-colors duration-150 md:block',
                isDeepStuck ? 'bg-background-default' : 'bg-transparent'
              )}
              data-testid="srp-quick-filters-sticky-mask"
            />
          )}
          <div
            className={cx(
              'absolute -bottom-xl inset-x-0 h-xl transition-colors duration-150 md:-bottom-2xl md:h-2xl',
              isStuck
                ? ['bg-background-primary-subtle', 'md:rounded-b-md']
                : 'bg-transparent'
            )}
            data-testid="srp-quick-filters-sticky-bottom-mask"
          />
          <TwoRowSuggestedFilters
            filterMenuChipTestId="open-filter-modal-quick-filters"
            isLoading={chipIsLoading}
            items={chipItems}
            showCounts={showCounts}
          />
        </div>
      </>
    );
  }

  if (!showSuggestedFilters) return null;

  return (
    <div
      className={cx(
        className,
        containerNegativeMargin,
        'relative mt-xl flex min-h-24 items-center overflow-hidden sm:mt-xl',
        { hidden: !isLoading && items.length === 0 }
      )}
      data-testid="srp-quick-filters"
      ref={containerRef}
    >
      <div
        className={cx(containerPadding, 'no-scrollbar flex overflow-x-auto')}
        ref={scrollRef}
      >
        <div className="flex items-center gap-md sm:flex-wrap ">
          <SuggestedFilterChips
            isLoading={isLoading}
            items={items}
            showCounts={showCounts}
          />
        </div>
      </div>
    </div>
  );
}

export default withErrorBoundary(QuickFilters, {
  section: 'SRP QuickFilters'
});
